import { useCallback, useEffect, useRef, useState } from "react";
import { Pressable, ScrollView, type View as RNView } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { sizes } from "@src/constants/sizes";

export interface SiteOption {
  siteId: string;
  siteName: string;
}

interface SiteDropdownProps {
  sites: SiteOption[];
  /** Selected site ids; empty means every site. */
  value: readonly string[];
  onChange: (siteIds: string[]) => void;
}

// Styled to sit beside SortDropdown in the camera toolbar.
export function SiteDropdown({ sites, value, onChange }: SiteDropdownProps) {
  const { theme } = useUnistyles();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<RNView>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      const node = containerRef.current as unknown as HTMLElement | null;
      if (node && !node.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen]);

  // Picking a site toggles it and leaves the menu open so several can be
  // picked; "All sites" clears the selection and closes it.
  const handleToggle = useCallback(
    (siteId: string | null) => {
      if (siteId === null) {
        onChange([]);
        setIsOpen(false);
        return;
      }
      onChange(value.includes(siteId) ? value.filter((id) => id !== siteId) : [...value, siteId]);
    },
    [onChange, value],
  );

  const label =
    value.length === 0 ? "All sites"
    : value.length === 1 ? (sites.find((site) => site.siteId === value[0])?.siteName ?? "1 site")
    : `${value.length} sites`;
  const options: { siteId: string | null; siteName: string }[] = [{ siteId: null, siteName: "All sites" }, ...sites];

  return (
    <View ref={containerRef} style={{ position: "relative", zIndex: 100 }}>
      <Pressable
        onPress={() => setIsOpen((prev) => !prev)}
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: theme.spacing.md,
          paddingHorizontal: theme.spacing["3xl"],
          borderWidth: 1,
          borderColor: isOpen ? theme.colors.blue[500] : theme.colors.border,
          borderRadius: sizes.radius,
          backgroundColor: theme.colors.surface,
          height: 44,
          maxWidth: 260,
        }}
      >
        <Icon name="building" size={theme.spacing["4xl"]} color={theme.colors.text} />
        <Text textStyle="sm/regular" color="text" numberOfLines={1} style={{ flexShrink: 1 }}>
          {label}
        </Text>
        <Icon name={isOpen ? "chevronUp" : "chevronDown"} size={theme.spacing["4xl"]} color={theme.colors.text} />
      </Pressable>

      {isOpen && (
        <View
          style={{
            position: "absolute",
            top: 48,
            right: 0,
            minWidth: 220,
            maxWidth: 320,
            backgroundColor: theme.colors.surface,
            borderRadius: sizes.radius,
            borderWidth: 1,
            borderColor: theme.colors.border,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.15,
            shadowRadius: 12,
            elevation: 8,
            zIndex: 1000,
            overflow: "hidden",
          }}
        >
          <ScrollView style={{ maxHeight: 320 }} showsVerticalScrollIndicator={false}>
            {options.map((option) => {
              const isSelected = option.siteId === null ? value.length === 0 : value.includes(option.siteId);
              return (
                <Pressable
                  key={option.siteId ?? "all"}
                  onPress={() => handleToggle(option.siteId)}
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: isSelected }}
                  style={({ hovered }: { hovered?: boolean }) => ({
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: theme.spacing.md,
                    paddingVertical: theme.spacing["3xl"],
                    paddingHorizontal: theme.spacing["4xl"],
                    backgroundColor: hovered ? theme.colors.muted : "transparent",
                  })}
                >
                  <Text
                    textStyle={isSelected ? "sm/semibold" : "sm/regular"}
                    color="text"
                    numberOfLines={1}
                    style={{ flexShrink: 1 }}
                  >
                    {option.siteName}
                  </Text>
                  <Icon
                    name="checkmark"
                    size={theme.spacing["4xl"]}
                    color={isSelected ? theme.colors.blue[500] : "transparent"}
                  />
                </Pressable>
              );
            })}
          </ScrollView>
        </View>
      )}
    </View>
  );
}
